import { combineGclidAndGaIdValue } from '@klv/shared/utils/google-tag-manager';
import { getUrlTrackingParams } from '@klv/shared/utils/functions';
import { getStoredTrackingParams } from '@klv/shared/utils/attribution';
import getVisitorGeolocation from '@klv/context/localization/utils';
import type { IFormField } from '../types';

export const getGclidValue = async (urlTrackingParams: Record<string, string | undefined>, storedTrackingParams: Record<string, string>): Promise<string> => {
  const gclid = urlTrackingParams?.gclid || '';
  const googleClientId = await combineGclidAndGaIdValue(gclid);
  const irclickid = urlTrackingParams?.irclickid || storedTrackingParams?.irclickid || '';
  return `${googleClientId}${irclickid ? `:${irclickid}` : ''}`;
};

export const getUtmParameters = (): IFormField[] => {
  const storedTrackingParams = getStoredTrackingParams();
  return Object.entries(storedTrackingParams)
    .filter(([_, value]) => value)
    .map(([key, value]) => ({
      name: key,
      value: value as string,
    }));
};

const getGclidField = async (): Promise<IFormField> => {
  const urlTrackingParams = getUrlTrackingParams();
  const storedTrackingParams = getStoredTrackingParams();
  const gclid = await getGclidValue(urlTrackingParams, storedTrackingParams);
  return ({
    name: 'gclid',
    value: gclid,
  });
};

export const getConversionData = (): IFormField[] => {
  const fields: IFormField[] = [
    {
      name: 'conversion_page',
      value: window.location.href,
    },
  ];

  const klaviyoPagesVisitCount = sessionStorage.getItem('klaviyoPagesVisitCount');
  if (klaviyoPagesVisitCount) {
    try {
      const pagesArray = JSON.parse(klaviyoPagesVisitCount);
      fields.push({
        name: 'web_pages_visited_prior_to_conversion',
        value: JSON.stringify(pagesArray),
      });
    } catch (error) {
      console.error('Error parsing klaviyoPagesVisitCount:', error);
    }
  }

  return fields;
};

export const getLocationData = async (): Promise<IFormField | null> => {
  const locationData = await getVisitorGeolocation();
  if (locationData && typeof locationData === 'object' && 'country' in locationData && locationData.country) {
    return {
      name: 'country_indicated_at_signup',
      value: locationData.country,
    };
  }
  return null;
};

export const setFormField = (form: HTMLElement, field: IFormField | null) => {
  if (!field) return;
  const input = form.querySelector(`input[name="${field.name}"]`) as HTMLInputElement;
  if (input) {
    input.value = field.value;
  }
};

export const populateFormFields = async (form: HTMLElement): Promise<void> => {
  try {
    const utmFields = getUtmParameters();
    const gclidField = await getGclidField();
    const conversionFields = getConversionData();
    const locationField = await getLocationData();

    [
      ...utmFields,
      gclidField,
      ...conversionFields,
      locationField,
    ].forEach(field => setFormField(form, field));
  } catch (error) {
    console.error('Error setting form fields:', error);
  }
};

export const validateFormCreation = (elementId: string, onError: () => void): boolean => {
  if (!window.hbspt?.forms?.create) {
    console.error('Hubspot forms API not available');
    return false;
  }

  const targetElement = document.getElementById(elementId);
  if (!targetElement) {
    console.error('Target element not found in DOM');
    onError();
    return false;
  }

  return true;
};

export const handleFormSubmission = (redirectUrl?: string): void => {
  if (redirectUrl) {
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 10);
  }
}; 
